import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Slide, useTheme, useMediaQuery } from '@mui/material';
import { Close, Login, ArrowForward } from '@mui/icons-material';
import CustomButton from './CustomButton';
import { glassStyles } from '../../tokens/designTokens';

interface FloatingLoginBarProps {
  onLogin: () => void;
  shareType: 'movie' | 'person';
  itemTitle?: string;
  sharedBy?: string;
  /** Logged-out public movie shell has no bottom nav — sit flush above safe area */
  bottomInsetVariant?: 'aboveBottomNav' | 'floating';
}

const DISMISS_STORAGE_KEY = 'floatingLoginBarDismissed';

const FloatingLoginBar: React.FC<FloatingLoginBarProps> = ({
  onLogin,
  shareType,
  itemTitle,
  sharedBy,
  bottomInsetVariant = 'aboveBottomNav',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dismissed, setDismissed] = useState(false);

  // Check if user dismissed the bar in this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem(DISMISS_STORAGE_KEY) === 'true';
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_STORAGE_KEY, 'true');
  };

  if (dismissed) return null;

  const getMessage = () => {
    if (sharedBy && itemTitle) {
      return `${sharedBy} shared ${shareType === 'movie' ? 'a movie' : 'an actor'} with you`;
    }
    return `Sign in to save ${shareType === 'movie' ? 'movies' : 'favorites'} and create watchlists`;
  };

  return (
    <Slide direction="up" in={!dismissed} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom:
            bottomInsetVariant === 'floating'
              ? isMobile
                ? 'calc(16px + var(--safe-area-inset-bottom))'
                : 24
              : isMobile
                ? 'calc(56px + env(safe-area-inset-bottom, 16px))'
                : 24,
          left: isMobile ? 16 : 'auto',
          right: 16,
          zIndex: 1300, // Above content, below modals
          maxWidth: isMobile ? 'calc(100% - 32px)' : 480,
          ...glassStyles.card(isDark),
          backdropFilter: 'blur(20px)',
          backgroundColor: isDark
            ? 'rgba(30, 30, 30, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
        role="banner"
        aria-label="Sign in prompt"
      >
        {/* Icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: '#7D7AFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <Login sx={{ color: '#323232', fontSize: 20 }} />
        </Box>

        {/* Message */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.4,
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {getMessage()}
          </Typography>
        </Box>

        {/* Sign In Button */}
        <CustomButton
          onClick={onLogin}
          semantic="brand"
          size="small"
          endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
          sx={{
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            px: 2,
            py: 0.75,
            minWidth: 'auto',
          }}
          aria-label="Sign in to access full features"
        >
          Sign In
        </CustomButton>

        {/* Dismiss Button */}
        <IconButton
          onClick={handleDismiss}
          size="small"
          sx={{
            flexShrink: 0,
            width: 28,
            height: 28,
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
          }}
          aria-label="Dismiss sign in prompt"
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Slide>
  );
};

export default FloatingLoginBar;
